import { useState, useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { v4 as uuidv4 } from "uuid";
import {
  ApiKeyCapability,
  IVenueApiKey,
  K8Namespace,
} from "@pulse-ui/codegen/generated_ts/authn";
import { FixSessionType } from "@pulse-ui/codegen/generated_ts/types";
import { trimStrings } from "@pulse-ui/formatting";
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { IAGPortfolioDataRow } from "@pulse-ui/types/components/AGTable";

import { useFetchAllStrategiesQuery } from "@/pages/StrategyManagement/services/strategyManagementApi";
import {
  useGetAccountStrategyQuery,
  useLinkAccountStrategyMutation,
  useUnlinkAccountStrategyMutation,
} from "@/services/accountStrategyApi";
import {
  useGetAccountQuery,
  useGetAccountApiKeysQuery,
  useGetVenueCodesQuery,
  useGetDropcopyIpsQuery,
  useUpdateAccountMutation,
  useSaveApiKeyMutation,
  useDeleteApiKeyMutation,
} from "@/pages/EntityManagement/services/entityManagementApi";

const NEW_KEY = "NEW_KEY";

interface IAccountForm {
  [key: string]: string | boolean | null;
  display_name: string;
  account_id: string;
  is_active: boolean;
  created: string;
  last_modified: string;
  strategy: string;
  strategy_id: string | null;
}

type TransformFields<T, FieldType> = {
  [K in keyof T]: FieldType;
};

type VenueApiKeyPayload = TransformFields<
  IVenueApiKey,
  string | string[] | null
>;

interface IApiKeyFormErrors {
  account_id: boolean;
  portfolio_id: boolean;
  access_key: boolean;
  secret_key: boolean;
  venue: boolean;
  allow_ip: boolean;
  api_capabilities: boolean;
}

export const useAccountModal = ({
  account_details,
  portfolio_id,
  dataMap,
  setDataMap,
  pushAlert,
  setSelectedAccountDetails,
  fetchVenueAccountsForExpandedRows,
}: {
  account_details: any;
  portfolio_id: string;
  dataMap: Map<string, IAGPortfolioDataRow>;
  setDataMap: React.Dispatch<
    React.SetStateAction<Map<string, IAGPortfolioDataRow>>
  >;
  pushAlert: (alert: IAlertMessage) => void;
  setSelectedAccountDetails: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  fetchVenueAccountsForExpandedRows: (
    portfolios_map: Map<string, IAGPortfolioDataRow>
  ) => Promise<Map<string, IAGPortfolioDataRow>>;
}) => {
  // States
  const [selectedApiKey, setSelectedApiKey] = useState<string | typeof NEW_KEY>(
    NEW_KEY
  );
  const [displayApiKeyForm, setDisplayApiKeyForm] = useState<boolean>(false);
  const [newApiKey, setNewApiKey] = useState<IVenueApiKey>({
    key_id: uuidv4(),
    account_id: "",
    portfolio_id: "",
    access_key: "",
    secret_key: "",
    passphrase: "",
    venue: "",
    connection: "",
    sender_comp_id: "",
    target_comp_id: "",
    availability_zone: "",
    allow_ip: "",
    venue_api_key_flags: [],
    api_capabilities: [],
    fix_session_type: null,
    namespaces: [K8Namespace.public_api],
    quoting_allow_ip: "",
  });
  const [isAccountEditMode, setIsAccountEditMode] = useState<boolean>(false);
  const [buttonDisableStatus, setButtonDisableStatus] = useState<boolean>(true);

  const [deleteModalStatus, setDeleteModalStatus] = useState<{
    open: boolean;
    item_id: string | null;
    item_name: string | null;
    entity_id: string | null;
  }>({
    open: false,
    item_id: null,
    item_name: null,
    entity_id: null,
  });

  const [accountForm, setAccountForm] = useState<IAccountForm>({
    display_name: "",
    account_id: "",
    is_active: false,
    created: "",
    last_modified: "",
    strategy: "",
    strategy_id: null,
  });

  const [clipboardSnackbar, setClipboardSnackbar] = useState<{
    open: boolean;
    success: boolean | null;
    message: string;
  }>({
    open: false,
    success: null,
    message: "",
  });

  // RTK Query hooks
  const { data: accountData } = useGetAccountQuery(
    { portfolioId: portfolio_id, accountId: account_details.account_id },
    { skip: !portfolio_id || !account_details.account_id }
  );

  const { data: apiKeysData } = useGetAccountApiKeysQuery(
    account_details?.account_id && account_details.account_id !== "undefined"
      ? account_details.account_id
      : skipToken
  );

  const { data: venueCodesData } = useGetVenueCodesQuery();
  const { data: ipAddressesData } = useGetDropcopyIpsQuery();

  const { data: strategies = [] } = useFetchAllStrategiesQuery();
  const { data: linkedStrategy, refetch: refetchLinkedStrategy } =
    useGetAccountStrategyQuery(account_details.account_id, {
      skip: !account_details.account_id,
    });
  const [linkStrategy, { isLoading: isLinkingStrategy }] = useLinkAccountStrategyMutation();
  const [unlinkStrategy, { isLoading: isUnlinkingStrategy }] = useUnlinkAccountStrategyMutation();

  const [updateAccount, { isLoading: isUpdatingAccount }] = useUpdateAccountMutation();
  const [saveApiKey, { isLoading: isSavingApiKey }] = useSaveApiKeyMutation();
  const [deleteApiKey, { isLoading: isDeletingApiKey }] = useDeleteApiKeyMutation();

  // Combine RTK Query loading states
  const isRTKLoading =
    isLinkingStrategy ||
    isUnlinkingStrategy ||
    isUpdatingAccount ||
    isSavingApiKey ||
    isDeletingApiKey;

  // Derived state
  const apiKeys = apiKeysData || [];
  const venueCodes = venueCodesData || [];
  const ipAddresses = ipAddressesData || [];

  // Methods
  const copyToClipboard = async (
    text: string,
    name: string
  ): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setClipboardSnackbar({
        open: true,
        success: true,
        message: `${name} copied to clipboard.`,
      });
    } catch (err) {
      setClipboardSnackbar({
        open: true,
        success: false,
        message: `Failed to copy ${name} to clipboard.`,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setClipboardSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleClose = () => {
    setSelectedAccountDetails(null);
    setAccountForm({
      display_name: "",
      account_id: "",
      is_active: false,
      created: "",
      last_modified: "",
      strategy: "",
      strategy_id: null,
    });
  };

  const handleAccountFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccountForm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSaveAccountEdits = async (account: IAccountForm) => {
    try {
      if (portfolio_id) {
        // Update account basic details
        await updateAccount({
          portfolioId: portfolio_id,
          accountId: accountForm.account_id,
          data: {
            account_id: accountForm.account_id.trim(),
            display_name: accountForm.display_name.trim(),
            created: accountForm.created.trim(),
            last_modified: accountForm.last_modified.trim(),
            is_active: accountForm.is_active,
            strategy: accountForm.strategy ? accountForm.strategy.trim() : null,
          },
        }).unwrap();

        // Handle strategy linking/unlinking
        const currentLinkedStrategyId = linkedStrategy?.strategy_id;
        const newStrategyId = accountForm.strategy_id;

        if (currentLinkedStrategyId !== newStrategyId) {
          // Unlink current strategy if there is one
          if (currentLinkedStrategyId) {
            await unlinkStrategy({
              accountId: accountForm.account_id,
              strategyId: currentLinkedStrategyId,
            }).unwrap();
          }

          // Link new strategy if one is selected
          if (newStrategyId) {
            await linkStrategy({
              accountId: accountForm.account_id,
              data: { strategy_id: newStrategyId },
            }).unwrap();
          }
        }

        // Refetch linked strategy
        await refetchLinkedStrategy();

        const venue_accounts_map = await fetchVenueAccountsForExpandedRows(
          dataMap
        );

        setDataMap(new Map(venue_accounts_map));
      } else {
        pushAlert({
          id: uuidv4(),
          message: `Error saving new account: ${account}. Selected portfolio not found.`,
          status: 500,
        });
        return;
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setIsAccountEditMode(false);
      handleClose();
    }
  };

  const handleDeleteApiKey = async (
    account_id: string | null,
    venue_api_key_id: string | null
  ) => {
    try {
      if (account_id && venue_api_key_id) {
        await deleteApiKey({
          accountId: account_id,
          keyId: venue_api_key_id,
        }).unwrap();
      } else {
        pushAlert({
          id: uuidv4(),
          message: `Failed to delete API Key ${venue_api_key_id}. Account or Venue API Key ID not found.`,
          status: 500,
        });
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setDeleteModalStatus({
        open: false,
        item_id: null,
        item_name: null,
        entity_id: null,
      });
      // Note: Don't setSelectedApiKey(NEW_KEY) here - let the useEffect handle it
      // after RTK Query refetch completes to avoid race condition
    }
  };

  const buildApiKeyPayload = (apiKey: IVenueApiKey): VenueApiKeyPayload => {
    return {
      key_id: apiKey.key_id || null,
      account_id: apiKey.account_id || null,
      portfolio_id: apiKey.portfolio_id || null,
      access_key: apiKey.access_key || null,
      secret_key: apiKey.secret_key,
      passphrase:
        apiKey.passphrase && apiKey.passphrase.length > 0
          ? apiKey.passphrase
          : null,
      venue: apiKey.venue,
      connection: apiKey.connection || null,
      sender_comp_id: apiKey.sender_comp_id || null,
      target_comp_id: apiKey.target_comp_id || null,
      availability_zone: apiKey.availability_zone || null,
      allow_ip: apiKey.allow_ip || null,
      venue_api_key_flags:
        (apiKey.venue_api_key_flags as unknown as string) || [],
      fix_session_type: (apiKey.fix_session_type as unknown as string) || null,
      api_capabilities:
        apiKey?.api_capabilities && apiKey.api_capabilities.length > 0
          ? apiKey.api_capabilities.map((capability: ApiKeyCapability) => {
              return ApiKeyCapability[capability];
            })
          : [],
      namespaces:
        apiKey?.namespaces && apiKey.namespaces.length > 0
          ? apiKey.namespaces.map((namespace: K8Namespace) => K8Namespace[namespace])
          : [K8Namespace[0]],
      quoting_allow_ip: apiKey?.quoting_allow_ip || null,
    };
  };

  const handleSaveApiKey = async () => {
    try {
      if (newApiKey.account_id) {
        const cleanSecretKey = newApiKey.secret_key
          ? newApiKey.secret_key
              .split("\n")
              .map((line: string) => line.trim())
              .join("\n")
          : newApiKey.secret_key;

        const apiKeyPayload = buildApiKeyPayload({
          ...newApiKey,
          secret_key: cleanSecretKey,
        });
        await saveApiKey({
          accountId: newApiKey.account_id,
          data: trimStrings(apiKeyPayload),
        }).unwrap();
      } else {
        pushAlert({
          id: uuidv4(),
          message: `Error saving new API Key: ${newApiKey.key_id}. Account ID not found.`,
          status: 500,
        });
        return;
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setSelectedApiKey(NEW_KEY);
      resetNewApiKey();
    }
  };

  const ipIsValid = (ip: string) => {
    if (ip.length < 1) return true;

    const ipv4Pattern =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

    return ipv4Pattern.test(ip);
  };

  const formIsValid = () => {
    let form_errors: IApiKeyFormErrors = {
      account_id: !newApiKey?.account_id || newApiKey.account_id.length < 1,
      portfolio_id:
        !newApiKey?.portfolio_id || newApiKey.portfolio_id.length < 1,
      access_key: !newApiKey?.access_key || newApiKey.access_key.length < 1,
      secret_key: !newApiKey?.secret_key || newApiKey.secret_key.length < 1,
      venue: !newApiKey?.venue || newApiKey.venue.length < 1,
      allow_ip:
        !newApiKey?.allow_ip ||
        newApiKey.allow_ip.length < 1 ||
        !ipIsValid(newApiKey.allow_ip || ""),
      api_capabilities:
        !newApiKey?.api_capabilities || newApiKey.api_capabilities.length < 1,
    };

    return !Object.values(form_errors).some((error) => error === true);
  };

  const handleNewApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "fix_session_type") {
      setNewApiKey({
        ...newApiKey,
        [name]: value === "" ? null : (value as unknown as FixSessionType),
      });
    } else {
      setNewApiKey({ ...newApiKey, [name]: value });
    }
  };

  const handleNewApiKeyFreeSoloChange = (value: any, name: string) => {
    setNewApiKey({
      ...newApiKey,
      [name]: value,
    });
  };

  const handleNewApiKeyVenueApiKeyFlagChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: any
  ) => {
    setNewApiKey({
      ...newApiKey,
      venue_api_key_flags: value,
    });
  };

  const handleNewApiKeyNamespacesChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: string[]
  ) => {
    setNewApiKey({
      ...newApiKey,
      namespaces: value.map(
        (namespace) => K8Namespace[namespace as keyof typeof K8Namespace]
      ),
    });
  };

  const handleNewApiKeyCapabilitiesChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: string[]
  ) => {
    setNewApiKey({
      ...newApiKey,
      api_capabilities: value.map(
        (capability) =>
          ApiKeyCapability[capability as keyof typeof ApiKeyCapability]
      ),
    });
  };

  const resetNewApiKey = () => {
    setNewApiKey({
      key_id: uuidv4(),
      account_id: account_details.account_id,
      portfolio_id: portfolio_id || "",
      access_key: "",
      secret_key: "",
      passphrase: "",
      venue: "",
      connection: "",
      sender_comp_id: "",
      target_comp_id: "",
      availability_zone: "",
      allow_ip: "",
      venue_api_key_flags: [],
      api_capabilities: [],
      fix_session_type: null,
      namespaces: [K8Namespace.public_api],
      quoting_allow_ip: "",
    });
  };

  // useEffects
  useEffect(() => {
    if (accountData) {
      setAccountForm({
        display_name: accountData.display_name || "",
        account_id: accountData.account_id || "",
        is_active: accountData.is_active || false,
        created: accountData.created || "",
        last_modified: accountData.last_modified || "",
        strategy: accountData.strategy || "",
        strategy_id: null,
      });
    }
  }, [accountData]);

  useEffect(() => {
    if (apiKeys.length === 0) {
      // No keys available - ensure we're on NEW_KEY and form is reset
      if (selectedApiKey !== NEW_KEY) {
        setSelectedApiKey(NEW_KEY);
        resetNewApiKey();
      }
    } else if (selectedApiKey === NEW_KEY) {
      // We have keys but no selection - auto-select first key
      setSelectedApiKey(apiKeys[0].venue_api_key_id);
    } else {
      // We have keys and a selection - verify it still exists
      const keyExists = apiKeys.some((key: any) => key.venue_api_key_id === selectedApiKey);
      if (!keyExists) {
        // Selected key was deleted, auto-select first available key
        setSelectedApiKey(apiKeys[0].venue_api_key_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeys, selectedApiKey]);

  useEffect(() => {
    if (linkedStrategy) {
      setAccountForm((prev) => ({
        ...prev,
        strategy_id: linkedStrategy.strategy_id,
      }));
    } else {
      setAccountForm((prev) => ({
        ...prev,
        strategy_id: null,
      }));
    }
  }, [linkedStrategy]);

  useEffect(() => {
    if (ipAddresses && ipAddresses.length > 0) {
      if (selectedApiKey === NEW_KEY) {
        resetNewApiKey();
      } else {
        const selectedApiKeyData: any = apiKeys.find(
          (api_key: any) => api_key.venue_api_key_id === selectedApiKey
        );

        if (selectedApiKeyData) {
          setNewApiKey({
            ...newApiKey,
            key_id: selectedApiKeyData?.venue_api_key_id || "",
            account_id: selectedApiKeyData?.account_id || "",
            portfolio_id: selectedApiKeyData?.portfolio_id || "",
            access_key: selectedApiKeyData?.key?.access_key || "",
            secret_key: selectedApiKeyData?.key?.secret_key || "",
            passphrase: selectedApiKeyData?.key?.passphrase || "",
            venue: selectedApiKeyData?.key?.venue || "",
            connection: selectedApiKeyData?.key?.connection || "",
            sender_comp_id: selectedApiKeyData?.key?.sender_comp_id || "",
            target_comp_id: selectedApiKeyData?.key?.target_comp_id || "",
            availability_zone: selectedApiKeyData?.key?.availability_zone || "",
            allow_ip: selectedApiKeyData?.key?.allow_ip || "",
            venue_api_key_flags:
              selectedApiKeyData?.key?.venue_api_key_flags || [],
            api_capabilities:
              selectedApiKeyData?.key?.api_capabilities &&
              selectedApiKeyData.key.api_capabilities.length > 0
                ? selectedApiKeyData.key.api_capabilities
                    .map((capability: string) => {
                      const enumKey = Object.keys(ApiKeyCapability).find(
                        (key) => key === capability
                      );
                      return enumKey !== undefined
                        ? ApiKeyCapability[
                            enumKey as keyof typeof ApiKeyCapability
                          ]
                        : undefined;
                    })
                    .filter((value: any) => value !== undefined)
                : [],
            fix_session_type:
              selectedApiKeyData.key.fix_session_type === ""
                ? null
                : (selectedApiKeyData.key
                    .fix_session_type as unknown as FixSessionType),
            namespaces:
              selectedApiKeyData?.key?.namespaces &&
              selectedApiKeyData.key.namespaces.length > 0
                ? selectedApiKeyData.key.namespaces
                    .map((namespace: string) => {
                      const enumKey = Object.keys(K8Namespace).find(
                        (key) => key === namespace
                      );
                      return enumKey !== undefined
                        ? K8Namespace[enumKey as keyof typeof K8Namespace]
                        : undefined;
                    })
                    .filter((value: any) => value !== undefined)
                : [K8Namespace.public_api],
            quoting_allow_ip: selectedApiKeyData?.key?.quoting_allow_ip || "",
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApiKey, ipAddresses]);

  useEffect(() => {
    setButtonDisableStatus(!formIsValid());
  }, [newApiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // State
    selectedApiKey,
    setSelectedApiKey,
    displayApiKeyForm,
    setDisplayApiKeyForm,
    newApiKey,
    isAccountEditMode,
    setIsAccountEditMode,
    buttonDisableStatus,
    deleteModalStatus,
    setDeleteModalStatus,
    accountForm,
    setAccountForm,
    clipboardSnackbar,

    // Loading state from RTK Query mutations
    isLoading: isRTKLoading,

    // Data
    apiKeys,
    venueCodes,
    ipAddresses,
    strategies,

    // Methods
    copyToClipboard,
    handleCloseSnackbar,
    handleClose,
    handleAccountFormChange,
    handleSaveAccountEdits,
    handleDeleteApiKey,
    handleSaveApiKey,
    handleNewApiKeyChange,
    handleNewApiKeyFreeSoloChange,
    handleNewApiKeyVenueApiKeyFlagChange,
    handleNewApiKeyNamespacesChange,
    handleNewApiKeyCapabilitiesChange,
  };
};
